/**
 * Projexino + tilesphere logo system — pure CSS/SVG, 3D animated.
 *
 * Identity concept:
 *  - Projexino: an interlocking "P" formed by two layered hexagonal nodes
 *    connected by a "nexus" line — represents projects connected across
 *    a precision network. Terracotta + ink palette.
 *  - tilesphere: same Projexino mark + "tilesphere" product wordmark; the
 *    mark slowly orbits and pulses to feel premium and tactile.
 *
 * Two variants:
 *  <Logo size={N} />            -> Full lockup (mark + wordmark "tilesphere")
 *  <Logo size={N} variant="mark" /> -> Mark only
 *  <Logo size={N} variant="projexino" /> -> Parent corporate wordmark
 */
export default function Logo({ size = 36, className = "", variant = "lockup", inverted = false }) {
  const ink = inverted ? "#F9F8F6" : "#1C1917";
  return (
    <div className={`pjx-logo inline-flex items-center gap-3 ${className}`} data-testid="brand-logo">
      <Mark size={size} ink={ink} />
      {variant === "lockup" && (
        <div className="flex flex-col leading-none">
          <span className="pjx-wordmark font-display font-black tracking-[-0.04em]" style={{ fontSize: `${size * 0.62}px`, color: ink }}>
            Tile<span style={{ color: "#C85A32" }}>ERP</span>
          </span>
          <span className="pjx-overline mt-1" style={{ color: inverted ? "#A8A29E" : "#78716C" }}>by Projexino</span>
        </div>
      )}
      {variant === "projexino" && (
        <span className="pjx-wordmark font-display font-black tracking-[-0.04em]" style={{ fontSize: `${size * 0.62}px`, color: ink }}>
          Projex<span style={{ color: "#C85A32" }}>ino</span>
        </span>
      )}
    </div>
  );
}

function Mark({ size, ink }) {
  const s = size;
  return (
    <div className="pjx-mark-stage" style={{ width: s, height: s }}>
      <svg viewBox="0 0 64 64" width={s} height={s} className="pjx-mark-svg" aria-hidden="true">
        <defs>
          <linearGradient id="pjxA" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#E8704A" />
            <stop offset="55%" stopColor="#C85A32" />
            <stop offset="100%" stopColor="#8A3A1F" />
          </linearGradient>
          <linearGradient id="pjxB" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#1C1917" />
            <stop offset="100%" stopColor="#3E3A35" />
          </linearGradient>
          <filter id="pjxShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1.5" stdDeviation="1.2" floodOpacity="0.22" />
          </filter>
        </defs>

        {/* back hex (ink) */}
        <g className="pjx-hex pjx-hex-back" filter="url(#pjxShadow)">
          <polygon points="20,6 44,6 56,28 44,50 20,50 8,28" fill="url(#pjxB)" />
        </g>

        {/* connecting nexus line */}
        <line className="pjx-nexus" x1="14" y1="40" x2="50" y2="24" stroke={ink} strokeWidth="2.5" strokeLinecap="round" opacity="0.55" />

        {/* front hex (terracotta) */}
        <g className="pjx-hex pjx-hex-front" filter="url(#pjxShadow)">
          <polygon points="26,14 50,14 62,36 50,58 26,58 14,36" fill="url(#pjxA)" />
        </g>

        {/* P glyph carved into front hex */}
        <g className="pjx-glyph" fill="#FFF8F2" stroke="rgba(0,0,0,0.18)" strokeWidth="0.6">
          {/* vertical stem of P */}
          <rect x="30" y="22" width="6" height="28" rx="1" />
          {/* bowl of P */}
          <path d="M36 22 L44 22 A8 8 0 0 1 44 38 L36 38 Z" />
          {/* nexus dot */}
          <circle className="pjx-dot" cx="33" cy="48" r="2.4" fill="#1C1917" />
        </g>
      </svg>
    </div>
  );
}
